import { useTranslation } from 'next-i18next'

import { useEffect, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import Input from '~/bootcamp/Activities/components/Input'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import { getRangesByDifficulty } from './constants'
import { generateTermsForFractionCancel } from './helpers'

function FractionCancel({
  answerState,
  difficultyLevel,
  onNextQuestionClick,
  onAnswerStateChange,
  onInstanceChange,
  autofocus = true,
  actionButtonsFC,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const [answer, setAnswer] = useState('')
  const [commonFactors, setCommonFactors] = useState<number[]>([])
  const [numerator, setNumerator] = useState<number>(1)
  const [denominator, setDenominator] = useState<number>(2)
  const [taskHistory, setTaskHistory] = useState<number[]>([])
  const [inputKey, setInputKey] = useState<string>(uuid())

  const { maxVariation } = getRangesByDifficulty(difficultyLevel)

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  // `commonFactors` stays out: it IS what the student must find.
  useEmitInstance(
    onInstanceChange,
    denominator
      ? `\\frac{${numerator}}{${denominator}}; a common factor of the numerator and the denominator = \\square`
      : null,
  )

  useEffect(() => {
    setAnswer('')
    const {
      numerator: num,
      denominator: denom,
      commonFactors: comnFact,
    } = generateTermsForFractionCancel(difficultyLevel, taskHistory)

    setNumerator(num)
    setDenominator(denom)
    setCommonFactors(comnFact)
  }, [difficultyLevel, taskHistory])

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    onAnswerStateChange(commonFactors.includes(parseInt(answer)))
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory((prevState) => {
      return [parseInt(answer), ...prevState].slice(0, maxVariation)
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (answer !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        <div className="flex flex-col items-center">
          <div className="w-8 flex flex-col items-center">
            {numerator}
            <Divider />
            {denominator}
          </div>
          <div className="pt-5">
            <Input
              aria-label={t('ariaLabelButtons.insertCommonFactor')}
              autoFocus={autofocus}
              isError={answerState === false}
              key={inputKey}
              maxDigits={4}
              onChange={(value) => setAnswer(value)}
              onEnter={onEnterPress}
              ref={inputRef}
              value={answer}
            />
          </div>
        </div>
      </FractionsActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer !== '',
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => setAnswer(''),
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

export default FractionCancel
